import React from "react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, BarChart, Bar, XAxis, YAxis, Cell, LabelList, Tooltip } from "recharts";
import axios from "axios";
import { Brand } from "@/components/Layout";
import { copeReport } from "@/lib/copeScoring";
import { COPE_T, LEVEL_COLORS, CATEGORY_COLORS } from "@/data/cope";
import { PatientClosing } from "@/components/patient/PatientClosing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RADAR_DOMAIN = [0, 6];
const TICK_INK = { fill: "#6E5C53", fontSize: 10 };
const TICK_MUTED = { fill: "#a1907f", fontSize: 9 };

const HEAD = {
  fr: {
    thanks: "Merci d'avoir complété ce questionnaire",
    radar: "Profil des stratégies de coping",
    radarSub: "14 stratégies · score de 0 à 6",
    table: "Tableau récapitulatif",
    strategy: "Stratégie",
    raw: "Score brut (/6)",
    pct: "% du maximum",
    level: "Niveau",
    ranking: "Classement des stratégies",
    rankingSub: "Du score le plus élevé au plus faible",
    profil: "Profil clinique",
    resources: "Ressources psychologiques potentielles",
    vulnerabilities: "Vulnérabilités potentielles",
    consequences: "Conséquences possibles du profil",
    work: "Travail",
    relations: "Relations",
    emotion: "Gestion émotionnelle",
    crisis: "Situations de crise",
    recommendations: "Recommandations thérapeutiques",
    contradictions: "Stratégies apparemment contradictoires",
    synthesis: "Synthèse clinique",
    noneRes: "Peu de ressources adaptatives ressortent nettement.",
    noneVul: "Aucune vulnérabilité marquée ne ressort.",
    noneContr: "Aucune contradiction notable n'a été repérée.",
  },
  es: {
    thanks: "Gracias por completar este cuestionario",
    radar: "Perfil de las estrategias de afrontamiento",
    radarSub: "14 estrategias · puntuación de 0 a 6",
    table: "Tabla resumen",
    strategy: "Estrategia",
    raw: "Puntuación bruta (/6)",
    pct: "% del máximo",
    level: "Nivel",
    ranking: "Clasificación de las estrategias",
    rankingSub: "De la puntuación más alta a la más baja",
    profil: "Perfil clínico",
    resources: "Recursos psicológicos potenciales",
    vulnerabilities: "Vulnerabilidades potenciales",
    consequences: "Posibles consecuencias del perfil",
    work: "Trabajo",
    relations: "Relaciones",
    emotion: "Manejo emocional",
    crisis: "Situaciones de crisis",
    recommendations: "Recomendaciones terapéuticas",
    contradictions: "Estrategias aparentemente contradictorias",
    synthesis: "Síntesis clínica",
    noneRes: "Destacan pocos recursos adaptativos.",
    noneVul: "No destaca ninguna vulnerabilidad marcada.",
    noneContr: "No se ha detectado ninguna contradicción notable.",
  },
};

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-6">
    <h3 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)]">{children}</h3>
    {sub && <p className="text-sm text-[var(--ink-soft)] mt-1">{sub}</p>}
  </div>
);

const Bullets = ({ items, empty }) =>
  items.length ? (
    <ul className="list-disc pl-6 space-y-2 text-[var(--ink)] leading-relaxed">
      {items.map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  ) : (
    <p className="text-sm text-[var(--ink-soft)]">{empty}</p>
  );

export const CopeResults = ({ answers, lang, situation = "", onRestart }) => {
  const t = COPE_T[lang];
  const h = HEAD[lang];
  const report = React.useMemo(() => copeReport(answers, lang, situation), [answers, lang, situation]);

  React.useEffect(() => {
    axios.post(`${API}/cope-evaluations`, { answers, lang, situation }).catch((e) => console.error("save cope failed", e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const radarData = report.rows.map((r) => ({ subject: r.name, value: r.score }));
  const balanceData = [
    { name: t.catAdaptative, value: report.categories.adaptative, cat: "adaptative" },
    { name: t.catAmbivalente, value: report.categories.ambivalente, cat: "ambivalente" },
    { name: t.catEvitante, value: report.categories.evitante, cat: "evitante" },
  ];
  const c = report.consequences;

  return (
    <div className="animate-fade-up print-container" data-testid="cope-results-view">
      <div className="hidden print:block mb-6"><Brand /></div>

      <div className="text-center mb-10">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{h.thanks}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.disclaimer}</p>
      </div>

      {/* Situation évaluée */}
      <div className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-4 sm:p-5 mb-8 print-break" data-testid="cope-situation-banner">
        <p className="text-xs uppercase tracking-[0.14em] text-[var(--gold)] mb-1">{t.situationReport}</p>
        <p className="text-[var(--ink)] leading-relaxed italic">{report.situation ? `« ${report.situation} »` : t.noSituation}</p>
      </div>

      {/* Équilibre adaptatif / évitant */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break">
        <SectionTitle sub={t.balanceSub}>{t.balanceTitle}</SectionTitle>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={balanceData} layout="vertical" margin={{ left: 20, right: 40 }}>
            <XAxis type="number" domain={[0, 6]} tick={TICK_INK} />
            <YAxis type="category" dataKey="name" width={160} tick={{ fill: "#4A3B36", fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${v}/6`, ""]} />
            <Bar dataKey="value" radius={[4, 4, 4, 4]} minPointSize={2}>
              {balanceData.map((d) => (<Cell key={d.cat} fill={CATEGORY_COLORS[d.cat]} />))}
              <LabelList dataKey="value" position="right" formatter={(v) => `${v}`} style={{ fill: "#6E5C53", fontSize: 12 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Radar */}
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 mb-10 print-break">
        <SectionTitle sub={h.radarSub}>{h.radar}</SectionTitle>
        <ResponsiveContainer width="100%" height={420}>
          <RadarChart data={radarData} outerRadius="70%">
            <PolarGrid stroke="#d8ccbe" />
            <PolarAngleAxis dataKey="subject" tick={TICK_INK} />
            <PolarRadiusAxis domain={RADAR_DOMAIN} tick={TICK_MUTED} angle={90} />
            <Radar dataKey="value" stroke="#B58C67" fill="#B58C67" fillOpacity={0.45} strokeWidth={2} />
            <Tooltip formatter={(v) => [`${v}/6`, ""]} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Tableau */}
      <div className="mb-10 print-break">
        <SectionTitle>{h.table}</SectionTitle>
        <div className="overflow-x-auto rounded-2xl border border-[var(--border)]">
          <table className="w-full text-sm" data-testid="cope-recap-table">
            <thead>
              <tr className="bg-[var(--gold)] text-white text-left">
                <th className="p-3 font-medium">{h.strategy}</th>
                <th className="p-3 font-medium text-right">{h.raw}</th>
                <th className="p-3 font-medium text-right">{h.pct}</th>
                <th className="p-3 font-medium">{h.level}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r, i) => (
                <tr key={r.key} className={i % 2 ? "bg-white/40" : "bg-white/70"}>
                  <td className="p-3 text-[var(--ink)]">{r.name}</td>
                  <td className="p-3 text-right font-semibold text-[var(--ink)]">{r.score}</td>
                  <td className="p-3 text-right text-[var(--ink-soft)]">{r.pct} %</td>
                  <td className="p-3">
                    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs text-white" style={{ backgroundColor: LEVEL_COLORS[r.level] }}>
                      {r.levelLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Classement */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break">
        <SectionTitle sub={h.rankingSub}>{h.ranking}</SectionTitle>
        <ol className="space-y-2" data-testid="cope-ranking">
          {report.ranked.map((r, i) => (
            <li key={r.key} className="flex items-center justify-between rounded-xl bg-[var(--sand)] border border-[var(--border)] px-4 py-2.5">
              <span className="text-[var(--ink)]"><span className="text-[var(--gold)] font-semibold mr-2">{i + 1}.</span>{r.name}</span>
              <span className="font-semibold" style={{ color: LEVEL_COLORS[r.level] }}>{r.score}/6</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Profil clinique */}
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-8 print-break">
        <h3 className="font-serif-title text-3xl text-[var(--gold)] mb-4">{h.profil}</h3>
        <p className="text-[var(--ink)] leading-relaxed" data-testid="cope-profil">{report.profil}</p>
      </div>

      {/* Ressources / Vulnérabilités */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 print-break">
          <h4 className="font-serif-title text-2xl mb-4" style={{ color: "#8A6D56" }}>{h.resources}</h4>
          <Bullets items={report.resources} empty={h.noneRes} />
        </div>
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 print-break">
          <h4 className="font-serif-title text-2xl mb-4" style={{ color: "#B0553B" }}>{h.vulnerabilities}</h4>
          <Bullets items={report.vulnerabilities} empty={h.noneVul} />
        </div>
      </div>

      {/* Conséquences */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-8 print-break">
        <h3 className="font-serif-title text-3xl text-[var(--gold)] mb-5">{h.consequences}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[["work", c.work], ["relations", c.relations], ["emotion", c.emotion], ["crisis", c.crisis]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-[var(--sand)] border border-[var(--border)] p-4">
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--gold)] mb-1">{h[k]}</p>
              <p className="text-sm text-[var(--ink)] leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recommandations */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-8 print-break">
        <h3 className="font-serif-title text-3xl text-[var(--gold)] mb-4">{h.recommendations}</h3>
        <Bullets items={report.recommendations} empty="" />
      </div>

      {/* Contradictions */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-8 print-break">
        <h3 className="font-serif-title text-3xl text-[var(--gold)] mb-4">{h.contradictions}</h3>
        <Bullets items={report.contradictions} empty={h.noneContr} />
      </div>

      {/* Synthèse */}
      <div className="rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-9 mb-10 print-break">
        <h3 className="font-serif-title text-3xl mb-4">{h.synthesis}</h3>
        <p className="leading-relaxed text-[0.98rem]" data-testid="cope-synthesis">{report.synthesis}</p>
      </div>

      <PatientClosing lang={lang} />

      <div className="flex flex-wrap items-center justify-center gap-4 no-print">
        <button onClick={() => window.print()} data-testid="cope-btn-export" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.exportPdf}</button>
        <button onClick={onRestart} data-testid="cope-btn-restart" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.restart}</button>
      </div>
    </div>
  );
};
